// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import { useState, useEffect } from 'react'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { 
  Building2,
  ArrowUpRight,
  ArrowDownLeft,
  AlertCircle,
  X,
  PiggyBank,
  Plus
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from "sonner"
import { useSavingGoalsStore } from '@flows.cash/core'
import { AccountType } from '@/types/account'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateTime } from '@/lib/dateUtils'
import { GoalTransaction } from '@flows.cash/types'
import AssignTransactionsDialog from './AssignTransactionsDialog'
import { formatCurrency } from '@/utils/formatters'

interface ExtendedSavingGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  target_date: string | null;
  account_id: string;
  icon: string | null;
  color: string | null;
  description: string | null;
  status?: 'active' | 'paused' | 'completed';
  is_completed: boolean;
  created_at: string;
  updated_at: string;
  account?: {
    id: string;
    name: string;
    type: AccountType;
    balance: number;
    currency?: string;
  };
}

interface TransactionsDialogProps {
  goal: ExtendedSavingGoal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransactionsDialog({ goal, open, onOpenChange }: TransactionsDialogProps) {
  const { contributeToGoal, withdrawFromGoal, isLoading, fetchGoalTransactions, unassignTransactionFromGoal } = useSavingGoalsStore()
  const [amount, setAmount] = useState('')
  const [goalTransactions, setGoalTransactions] = useState<GoalTransaction[]>([])
  const [activeTab, setActiveTab] = useState('view')
  const [loadingTransactions, setLoadingTransactions] = useState(false)
  const [removingTransactionId, setRemovingTransactionId] = useState<string | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  
  // Helper for formatting currency amounts
  const formatAmount = (amount: number) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: goal.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace(/[^\d.,]/g, '');
  }
  
  // Get currency symbol based on goal currency
  const getCurrencySymbol = (currency: string) => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency }).formatToParts(0).find(part => part.type === 'currency')?.value || '$'
    } catch (e) {
      return '$'
    }
  }
  
  const currencySymbol = getCurrencySymbol(goal.currency)
  
  // Fetch assigned transactions when dialog opens
  useEffect(() => {
    if (open) {
      loadTransactions();
    }
  }, [open, goal.id])

  const loadTransactions = async () => {
    if (!goal.id) return;
    
    try {
      setLoadingTransactions(true);
      const data = await fetchGoalTransactions(goal.id);
      setGoalTransactions(data);
    } catch (error) {
      console.error('Error fetching goal transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };
  
  const handleUnassignTransaction = async (transactionId: string) => {
    try {
      setRemovingTransactionId(transactionId);
      await unassignTransactionFromGoal({ goalId: goal.id, transactionId });
      setGoalTransactions(goalTransactions.filter(gt => gt.transaction_id !== transactionId));
      toast.success('Transaction unassigned successfully');
    } catch (error) {
      console.error('Error unassigning transaction:', error);
      toast.error('Failed to unassign transaction');
    } finally {
      setRemovingTransactionId(null);
    }
  };
  
  const handleContribute = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount')
      return
    }
    
    try {
      await contributeToGoal(goal.id, parseFloat(amount))
      setAmount('')
      setActiveTab('view')
    } catch (error) {
      console.error('Error contributing to goal:', error)
      toast.error('Failed to contribute to goal')
    }
  }
  
  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount')
      return
    }
    
    if (parseFloat(amount) > goal.current_amount) {
      toast.error('Cannot withdraw more than the current amount')
      return
    }
    
    try {
      await withdrawFromGoal(goal.id, parseFloat(amount))
      setAmount('')
      setActiveTab('view')
    } catch (error) {
      console.error('Error withdrawing from goal:', error)
      toast.error('Failed to withdraw from goal')
    }
  }

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    loadTransactions(); // Refresh transactions after assigning new ones
  };
  
  // Calculate progress percentage
  const progressPercentage = Math.min((goal.current_amount / goal.target_amount) * 100, 100);
  
  return (
    <>
      <Dialog open={open} onOpenChange={(newOpen) => {
        if (!newOpen) {
          setAmount('')
          setActiveTab('view')
        }
        onOpenChange(newOpen)
      }}>
        <DialogContent className="max-w-md bg-background-secondary dark:bg-background-dark-primary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl p-0 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 dark:from-primary-dark/10 dark:to-secondary-dark/10 px-6 pt-6 pb-4 border-b border-border dark:border-border-dark">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-text-primary dark:text-text-dark-primary bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
                {goal.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex items-center gap-2 mt-2">
              {goal.account && (
                <div className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
                  <Building2 className="w-4 h-4" />
                  <span>{goal.account.name}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="px-6 py-4 bg-background-input/50 dark:bg-background-dark-secondary/30">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-background-secondary dark:bg-background-dark-secondary rounded-xl p-3 shadow-sm border border-border dark:border-border-dark">
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Current</p>
                <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                  {currencySymbol}{formatAmount(goal.current_amount)}
                </p>
              </div>
              <div className="bg-background-secondary dark:bg-background-dark-secondary rounded-xl p-3 shadow-sm border border-border dark:border-border-dark">
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Target</p>
                <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                  {currencySymbol}{formatAmount(goal.target_amount)}
                </p>
              </div>
            </div>
            
            {/* Enhanced progress bar with border and better dark mode support */}
            <div className="w-full bg-background-secondary dark:bg-background-dark-secondary rounded-full h-3 mb-2 relative shadow-inner overflow-hidden border border-border dark:border-border-dark">
              <div 
                className={cn(
                  "absolute top-0 left-0 h-full rounded-full transition-all duration-300 ease-out",
                  "bg-gradient-to-r from-secondary to-secondary/90 dark:from-primary-dark dark:to-primary-dark/90",
                  "shadow-[0_0_10px_rgba(67,148,118,0.3)] dark:shadow-[0_0_10px_rgba(67,148,118,0.2)]"
                )}
                style={{ width: `${progressPercentage}%` }}
              />
              {progressPercentage > 10 && (
                <div className="h-full w-full absolute top-0 left-0 opacity-30">
                  <div className="h-[1px] w-full bg-white/20 absolute top-[25%]" />
                  <div className="h-[1px] w-full bg-white/20 absolute top-[50%]" />
                </div>
              )}
            </div>
            <div className="text-xs text-right text-text-secondary dark:text-text-dark-secondary mb-1 font-medium">
              {Math.round(progressPercentage)}% complete
            </div>
          </div>
          
          <div className="px-6 py-4 bg-background-secondary dark:bg-background-dark-primary">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-3 mb-4 bg-background-input dark:bg-background-dark-secondary">
                {["view", "contribute", "withdraw"].map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className={cn(
                      "capitalize",
                      activeTab === tab && "bg-background-secondary dark:bg-background-dark-primary shadow-sm"
                    )}
                  >
                    {tab}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              <TabsContent value="view" className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Transactions</h3>
                  <Button 
                    size="sm" 
                    onClick={() => setAssignDialogOpen(true)}
                    className="flex items-center gap-1 h-8 bg-secondary hover:bg-secondary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white shadow-md hover:shadow-lg transition-all"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span className="text-xs">Add transactions</span>
                  </Button>
                </div>
                
                {loadingTransactions ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-secondary dark:border-primary-dark" />
                  </div>
                ) : goalTransactions.length > 0 ? (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                    {goalTransactions.map((goalTransaction) => {
                      const transaction = goalTransaction.transaction;
                      if (!transaction) return null;
                      
                      return (
                        <div 
                          key={goalTransaction.id} 
                          className="p-3 border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary rounded-xl flex justify-between items-center hover:border-secondary/50 dark:hover:border-primary-dark/50 transition-colors shadow-sm"
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center",
                              transaction.type === 'transfer' 
                                ? 'bg-[#6366F1]/10 dark:bg-[#6366F1]/20 text-[#6366F1] dark:text-[#6366F1]'
                                : transaction.type === 'expense' 
                                  ? 'bg-negative/10 dark:bg-negative-dark/20 text-negative dark:text-negative-dark' 
                                  : 'bg-positive/10 dark:bg-positive-dark/20 text-positive dark:text-positive-dark'
                            )}>
                              {transaction.type === 'income' ? (
                                <ArrowUpRight className="w-4 h-4" />
                              ) : transaction.type === 'expense' ? (
                                <ArrowDownLeft className="w-4 h-4" />
                              ) : (
                                <PiggyBank className="w-4 h-4" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                                {transaction.description || 'No description'}
                              </p>
                              <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                                {formatDateTime(new Date(transaction.date), {})}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              "font-medium text-sm",
                              transaction.type === 'income' ? "text-positive dark:text-positive-dark" : 
                              transaction.type === 'expense' ? "text-negative dark:text-negative-dark" : 
                              "text-primary dark:text-primary-dark"
                            )}>
                              {formatCurrency(transaction.amount, transaction.currency)}
                            </p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-full hover:bg-negative/10 hover:text-negative dark:hover:bg-negative-dark/10 dark:hover:text-negative-dark"
                              onClick={() => handleUnassignTransaction(transaction.id)}
                              disabled={removingTransactionId === transaction.id}
                            >
                              {removingTransactionId === transaction.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-negative dark:border-negative-dark" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 my-4 bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 rounded-xl border border-border dark:border-border-dark shadow-sm">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-text-secondary dark:text-text-dark-secondary opacity-50" />
                    <p className="font-medium text-text-primary dark:text-text-dark-primary">No transactions found</p>
                    <p className="text-sm mt-1 text-text-secondary dark:text-text-dark-secondary mb-4">Assign transactions to this goal to track your saving progress</p>
                    <Button 
                      onClick={() => setAssignDialogOpen(true)}
                      className="bg-secondary hover:bg-secondary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white rounded-xl px-4 py-2 text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                    >
                      Add transactions
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="contribute">
                <form onSubmit={handleContribute} className="space-y-4 bg-background-secondary dark:bg-background-dark-secondary p-4 rounded-xl shadow-sm border border-border dark:border-border-dark">
                  <div className="space-y-2">
                    <Label htmlFor="contribute-amount" className="text-text-primary dark:text-text-dark-primary">
                      Amount to contribute
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary">
                        {currencySymbol}
                      </span>
                      <Input
                        id="contribute-amount"
                        type="number"
                        placeholder="0"
                        min="0"
                        step="any"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-8 h-11 w-full rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full bg-gradient-to-r from-secondary to-secondary/90 hover:from-secondary/90 hover:to-secondary/80 dark:from-primary-dark dark:to-primary-dark/90 dark:hover:from-primary-dark/90 dark:hover:to-primary-dark/80 text-white rounded-xl h-11 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      'Contribute'
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="withdraw">
                <form onSubmit={handleWithdraw} className="space-y-4 bg-background-secondary dark:bg-background-dark-secondary p-4 rounded-xl shadow-sm border border-border dark:border-border-dark">
                  <div className="space-y-2">
                    <Label htmlFor="withdraw-amount" className="text-text-primary dark:text-text-dark-primary">
                      Amount to withdraw
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary">
                        {currencySymbol}
                      </span>
                      <Input
                        id="withdraw-amount"
                        type="number"
                        placeholder="0"
                        min="0"
                        max={goal.current_amount}
                        step="any"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-8 h-11 w-full rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
                      />
                    </div>
                    <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                      Available: {currencySymbol}{formatAmount(goal.current_amount)}
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={isLoading || parseFloat(amount) > goal.current_amount} 
                    className="w-full bg-gradient-to-r from-negative to-negative/90 hover:from-negative/90 hover:to-negative/80 dark:from-negative-dark dark:to-negative-dark/90 dark:hover:from-negative-dark/90 dark:hover:to-negative-dark/80 text-white rounded-xl h-11 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                  >
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      'Withdraw'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog for assigning transactions */}
      {assignDialogOpen && (
        <AssignTransactionsDialog 
          isOpen={assignDialogOpen} 
          onClose={handleAssignDialogClose} 
          goal={{
            id: goal.id,
            name: goal.name,
            current_amount: goal.current_amount,
            target_amount: goal.target_amount,
            currency: goal.currency
          }}
        />
      )}
    </>
  );
}
